import React, { useState, useEffect } from 'react';
import { string, func, arrayOf, shape, oneOfType, node, object } from 'prop-types';

import { CoachMarks as AndesCoachMarks } from '@andes/coach-marks';

import useTimesViewed from '../../../hooks/use-times-viewed';

/**
 * @typedef {Object} CoachStep
 * @property {string} title - Title of the step.
 * @property {string|JSX.Element} content - Content of the step.
 * @property {string} placement - Position of the tooltip (e.g. 'top', 'bottom').
 * @property {string} target - CSS selector of the target element.
 */

/**
 * Component to display an interactive coach marks tour.
 *
 * @param {Object} props
 * @param {string} props.localStorageKey - LocalStorage key to count views.
 * @param {Function} [props.onChange] - Optional callback after each status change.
 * @param {CoachStep[]} props.steps - Array of steps for the tour.
 * @param {Object} [props.locale] - Locale configuration for Andes CoachMarks.
 */
const CoachMarks = ({ localStorageKey, onChange, steps, locale }) => {
  const { isAvailable, handleClose, wasClosed, views } = useTimesViewed(localStorageKey);
  const [runCoachMarks, setRunCoachMarks] = useState(false);

  const onChangeHandler = ({ status }) => {
    onChange?.();

    if (['skipped', 'finished'].includes(status)) {
      handleClose();
      setRunCoachMarks(false);
    }
  };

  const completeSteps = steps?.map((step) => ({
    title: step.title,
    content: step.content,
    placement: step.placement,
    target: step.target,
    disableScrolling: false,
  }));

  useEffect(() => {
    if (views === 0 || !isAvailable || !steps?.length || wasClosed) {
      return;
    }

    const checkTargetsExist = () => {
      const firstTarget = completeSteps?.[0]?.target;

      if (!firstTarget) {
        return;
      }

      const targetElement = document.querySelector(firstTarget);

      if (targetElement) {
        setRunCoachMarks(true);
      } else {
        requestAnimationFrame(checkTargetsExist);
      }
    };

    checkTargetsExist();
  }, [isAvailable, steps, completeSteps, wasClosed, views]);

  return <AndesCoachMarks run={runCoachMarks} onChange={onChangeHandler} steps={completeSteps} locale={locale} />;
};

export { CoachMarks };

CoachMarks.propTypes = {
  locale: object,
  localStorageKey: string.isRequired,
  steps: arrayOf(
    shape({
      title: string.isRequired,
      content: oneOfType([string, node]).isRequired,
      placement: string,
      target: string.isRequired,
    }),
  ).isRequired,
  onChange: func,
};

CoachMarks.defaultProps = {
  onChange: undefined,
  locale: {},
};
